export const DISPLAY_NAME_MIN_LENGTH = 2;
export const DISPLAY_NAME_MAX_LENGTH = 24;

export const DISPLAY_NAME_MESSAGES = {
  required: 'Enter a display name',
  tooShort: `Use at least ${DISPLAY_NAME_MIN_LENGTH} characters`,
  tooLong: `Use ${DISPLAY_NAME_MAX_LENGTH} characters or fewer`,
  invalidCharacters: 'Use letters, numbers, emoji and punctuation only',
  /** Moderation's only answer: it never says which rule matched. */
  unavailable: "That name isn't available. Try another.",
  tooManyChanges: "You've changed your name too many times today. Try again tomorrow.",
} as const;

/** Every run of spaces (any Unicode space separator) at the start, the end or in between. */
const SPACE_RUNS = /\p{Zs}+/gu;

/** Control characters: tab, newline, escape and the rest of Unicode's Cc category. */
const CONTROL = /\p{Cc}/u;

/**
 * Characters that take up no space or show as blank: zero-width space, non-joiner and joiner, word joiner, byte order
 * mark, Mongolian vowel separator, soft hyphen, combining grapheme joiner, and the Hangul and Braille blanks.
 */
const INVISIBLE = new Set([
  0x00ad, 0x034f, 0x115f, 0x1160, 0x180e, 0x200b, 0x200c, 0x200d, 0x2060, 0x2800, 0x3164, 0xfeff, 0xffa0,
]);

const hasInvisible = (value: string): boolean =>
  Array.from(value).some((character) => INVISIBLE.has(character.codePointAt(0) ?? 0));

/** Characters that change the direction of the text around them: marks, embeddings, overrides and isolates. */
const TEXT_DIRECTION = /[\u061C\u200E\u200F\u202A-\u202E\u2066-\u2069]/u;

/**
 * An emoji ZWJ sequence such as 👨‍👩‍👧 or 🏳️‍🌈: pictographs joined by zero-width joiners, each with an optional
 * emoji presentation selector or skin tone. The joiner is allowed here and nowhere else.
 */
const EMOJI_ZWJ_SEQUENCE =
  /^\p{Extended_Pictographic}[\uFE0F\u{1F3FB}-\u{1F3FF}]?(?:\u200D\p{Extended_Pictographic}[\uFE0F\u{1F3FB}-\u{1F3FF}]?)+$/u;

const graphemes = new Intl.Segmenter(undefined, { granularity: 'grapheme' });

const segments = (value: string): string[] => Array.from(graphemes.segment(value), ({ segment }) => segment);

/** Characters as a person counts them: an emoji or an accented letter is one. */
export const countCharacters = (value: string): number => segments(value).length;

/**
 * The name as it is saved: composed characters (NFC), no spaces at the start or end, and each run of spaces inside
 * it collapsed to one ordinary space.
 */
export const normaliseDisplayName = (value: string): string =>
  value.normalize('NFC').replace(SPACE_RUNS, ' ').replace(/^ | $/g, '');

const isForbiddenSegment = (segment: string): boolean => {
  if (CONTROL.test(segment) || TEXT_DIRECTION.test(segment)) {
    return true;
  }
  const withoutEmojiJoiners = EMOJI_ZWJ_SEQUENCE.test(segment) ? segment.replaceAll('\u200D', '') : segment;
  return hasInvisible(withoutEmojiJoiners);
};

/** Whether the name holds a control, invisible or text-direction character, outside an emoji ZWJ sequence. */
export const hasForbiddenCharacters = (value: string): boolean => segments(value).some(isForbiddenSegment);

/**
 * Why the name can't be used, or undefined when it can. Checks the normalised name: required, no control, invisible
 * or text-direction characters, and 2–24 characters as a person counts them.
 */
export const validateDisplayName = (value: string): string | undefined => {
  const name = normaliseDisplayName(value);
  if (!name) return DISPLAY_NAME_MESSAGES.required;
  if (hasForbiddenCharacters(name)) return DISPLAY_NAME_MESSAGES.invalidCharacters;

  const length = countCharacters(name);
  if (length < DISPLAY_NAME_MIN_LENGTH) return DISPLAY_NAME_MESSAGES.tooShort;
  if (length > DISPLAY_NAME_MAX_LENGTH) return DISPLAY_NAME_MESSAGES.tooLong;
  return undefined;
};

export type DisplayNameCheck = { valid: true; displayName: string } | { valid: false; message: string };

/** The normalised name when it is valid, or the reason it isn't. */
export const checkDisplayName = (value: string): DisplayNameCheck => {
  const message = validateDisplayName(value);
  return message ? { valid: false, message } : { valid: true, displayName: normaliseDisplayName(value) };
};

/**
 * The number in each display-name error code from words-happen-service, `<serviceCode>:<number>`. The service code
 * is the running app's, so callers match on the number.
 */
export const DISPLAY_NAME_ERROR_NUMBERS = {
  invalid: '012',
  unavailable: '013',
  tooManyChanges: '014',
} as const;
